import * as React from 'react';
import { Check, ChevronDown, Search, Wrench } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

export type ComboboxOption = {
  value: string;
  label: string;
  iconUrl?: string;
  iconNode?: React.ReactNode;
  keywords?: string[];
  /** Encabezado de grupo opcional; las opciones sin grupo se muestran primero */
  group?: string;
};

interface SearchableComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onSelect: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  triggerClassName?: string;
  contentClassName?: string;
  excludeValues?: string[];
  triggerIcon?: React.ReactNode;
  /** Cuando es true, el trigger funciona como "Agregar..." y no muestra el valor seleccionado */
  addMode?: boolean;
  disabled?: boolean;
}

export function SearchableCombobox({
  options,
  value,
  onSelect,
  placeholder = 'Selecciona una opción',
  searchPlaceholder = 'Buscar...',
  emptyText = 'No se encontraron resultados',
  triggerClassName,
  contentClassName,
  excludeValues = [],
  triggerIcon,
  addMode = false,
  disabled = false,
}: SearchableComboboxProps) {
  const [open, setOpen] = React.useState(false);

  const filteredOptions = React.useMemo(
    () => options.filter((o) => !excludeValues.includes(o.value)),
    [options, excludeValues]
  );

  // Agrupa preservando el orden de aparición; las opciones sin grupo van primero.
  const groupedOptions = React.useMemo(() => {
    const map = new Map<string, ComboboxOption[]>();
    for (const opt of filteredOptions) {
      const key = opt.group ?? '';
      const list = map.get(key);
      if (list) list.push(opt);
      else map.set(key, [opt]);
    }
    return Array.from(map.entries());
  }, [filteredOptions]);



  const selected = options.find((o) => o.value === value);

  const renderIcon = (opt: ComboboxOption, size = 'w-4 h-4') => {
    if (opt.iconUrl) {
      return <img src={opt.iconUrl} alt="" className={cn(size, 'rounded object-contain')} />;
    }
    if (opt.iconNode) return opt.iconNode;
    return null;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between gap-2 font-normal',
            !selected && !addMode && 'text-muted-foreground',
            triggerClassName
          )}
        >
          <span className="flex items-center gap-2 truncate">
            {addMode ? (
              <>
                {triggerIcon ?? <Wrench className="w-4 h-4" />}
                <span className="truncate">{placeholder}</span>
              </>
            ) : selected ? (
              <>
                {renderIcon(selected)}
                <span className="truncate">{selected.label}</span>
              </>
            ) : (
              <>
                {triggerIcon}
                <span className="truncate">{placeholder}</span>
              </>
            )}
          </span>
          <ChevronDown
            className={cn('w-4 h-4 shrink-0 opacity-50 transition-transform', open && 'rotate-180')}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'p-0 w-[--radix-popover-trigger-width] min-w-[240px] bg-popover z-50',
          contentClassName
        )}
        align="start"
      >
        <Command
          filter={(itemValue, search, keywords) => {
            const haystack = `${itemValue} ${(keywords ?? []).join(' ')}`.toLowerCase();
            return haystack.includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className="max-h-64">
            <CommandEmpty>
              <div className="flex flex-col items-center gap-1 py-2 text-sm text-muted-foreground">
                <Search className="w-4 h-4 opacity-50" />
                {emptyText}
              </div>
            </CommandEmpty>
            {groupedOptions.map(([groupLabel, opts]) => (
              <CommandGroup key={groupLabel || '__ungrouped__'} heading={groupLabel || undefined}>
                {opts.map((opt) => (
                  <CommandItem
                    key={opt.value}
                    value={opt.label}
                    keywords={opt.keywords}
                    onSelect={() => {
                      onSelect(opt.value);
                      setOpen(false);
                    }}
                    className="gap-2 cursor-pointer"
                  >
                    {renderIcon(opt) ?? <Wrench className="w-4 h-4 opacity-50" />}
                    <span className="flex-1 truncate">{opt.label}</span>
                    {value === opt.value && <Check className="w-4 h-4 opacity-70" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
